import CloseIcon from '@mui/icons-material/Close';
import { Box, Divider, Fade, IconButton, Paper, Stack, Typography } from '@mui/material';
import React from 'react';

/**
 * Floating action bar that appears at the bottom of a grid when rows are selected.
 *
 * Renders as a centered pill that slides up with: "N Selected | [action buttons] | ×"
 *
 * Props:
 *   count    – number of selected rows (bar is hidden when 0)
 *   onClear  – callback to deselect all
 *   children – action buttons (Button, IconButton, etc.)
 */
export default function SelectionActionBar({ count, onClear, selectAll = false, children }) {
  return (
    <Fade in={count > 0} unmountOnExit>
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
      >
        <Paper
          elevation={8}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderRadius: 99,
            px: 2.5,
            py: 0.75,
            bgcolor: 'var(--selection-bar-bg)',
            color: 'var(--selection-bar-fg)',
            whiteSpace: 'nowrap',
            // The bar is fixed dark chrome, so it owns its buttons' styling:
            // the theme's raised light surfaces, light hover fill, and light
            // disabled fill are built for light pages and glare here. Quiet
            // transparent buttons, white-alpha hover, dimmed disabled text.
            '& .MuiButton-root': {
              color: 'var(--selection-bar-fg)',
              backgroundColor: 'transparent',
              border: 'none',
              boxShadow: 'none',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'var(--selection-bar-hover)',
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                backgroundColor: 'transparent',
                color: 'var(--selection-bar-disabled-fg)',
              },
            },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {selectAll ? `All ${count.toLocaleString()}` : count} Selected
          </Typography>

          <Divider orientation="vertical" flexItem sx={{ borderColor: 'var(--selection-bar-divider)' }} />

          {children}

          <Divider orientation="vertical" flexItem sx={{ borderColor: 'var(--selection-bar-divider)' }} />

          <IconButton size="small" onClick={onClear} sx={{ color: 'inherit' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>
      </Box>
    </Fade>
  );
}
